import { cn } from "../../lib/utils";

interface LoadingSpinnerProps {
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  message?: string;
  className?: string;
  variant?: "default" | "inline" | "overlay" | "skeleton";
  showLogo?: boolean;
  fullHeight?: boolean;
}

/**
 * Enhanced loading spinner component with multiple variants
 *
 * @param size - Spinner size: "xs" (6), "sm" (8), "md" (12), "lg" (16), "xl" (20) - default is "md"
 * @param message - Optional loading message to display
 * @param className - Optional additional CSS classes
 * @param variant - Display variant: "default" (centered), "inline" (inline), "overlay" (full overlay), "skeleton" (skeleton loader)
 * @param showLogo - Whether to show the CareQuote logo - default is true for default variant
 * @param fullHeight - Whether to use full height (h-screen) instead of h-64 - default is false
 *
 * @example
 * <LoadingSpinner size="lg" message="Loading facilities..." />
 * <LoadingSpinner variant="inline" size="sm" />
 * <LoadingSpinner variant="overlay" message="Saving..." />
 */
export function LoadingSpinner({
  size = "md",
  message,
  className,
  variant = "default",
  showLogo,
  fullHeight = false,
}: LoadingSpinnerProps) {
  const sizeClasses = {
    xs: "h-6 w-6",
    sm: "h-8 w-8",
    md: "h-12 w-12",
    lg: "h-16 w-16",
    xl: "h-20 w-20",
  };

  const spinner = (
    <div
      className={cn(
        "animate-spin rounded-full border-2 border-gray-300 border-t-primary",
        sizeClasses[size]
      )}
    ></div>
  );

  // Inline variant for buttons, small spaces
  if (variant === "inline") {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {spinner}
        {message && <span className="text-sm text-gray-600">{message}</span>}
      </div>
    );
  }

  // Overlay variant for full-screen or component overlays
  if (variant === "overlay") {
    return (
      <div className={cn(
        "absolute inset-0 bg-white bg-opacity-75 flex flex-col items-center justify-center z-10",
        className
      )}>
        {(showLogo !== false) && (
          <img
            src="/care-quote-logo.png"
            alt="Care Quote logo"
            className="w-12 h-12 object-contain mb-4"
          />
        )}
        {spinner}
        {message && <p className="mt-4 text-sm text-gray-600 text-center">{message}</p>}
      </div>
    );
  }

  // Skeleton variant for content placeholders
  if (variant === "skeleton") {
    return (
      <div className={cn("animate-pulse", className)}>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  // Default centered variant
  const shouldShowLogo = showLogo !== false && (size === "lg" || size === "xl");

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center",
        fullHeight ? "h-screen" : "h-64",
        className
      )}
    >
      {shouldShowLogo && (
        <img
          src="/care-quote-logo.png"
          alt="Care Quote logo"
          className="w-16 h-16 object-contain mb-4"
        />
      )}
      {spinner}
      {message && <p className="mt-4 text-gray-600 text-center">{message}</p>}
    </div>
  );
}

// Skeleton loader components for common UI patterns
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn("animate-pulse bg-white p-6 rounded-lg border", className)}>
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        <div className="h-8 bg-gray-200 rounded w-1/4 mt-4"></div>
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn("animate-pulse", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex space-x-4 py-3 border-b border-gray-100">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/6"></div>
          <div className="h-4 bg-gray-200 rounded w-1/5"></div>
        </div>
      ))}
    </div>
  );
}
